import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { OrderForm } from "@/components/OrderForm";
import { OrdersTable } from "@/components/OrdersTable";
import { MismatchReviewModal } from "@/components/MismatchReviewModal";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { CartPanel } from "@/components/CartPanel";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, ShieldOff, ArrowRight, Loader2, Settings2, XCircle } from "lucide-react";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, membership, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quota, setQuota] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [config, setConfig] = useState(null);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [privacyOptOut, setPrivacyOptOut] = useState(null);
  const [privacySaving, setPrivacySaving] = useState(false);

  const togglePrivacyOptOut = async () => {
    setPrivacySaving(true);
    try {
      const next = !privacyOptOut;
      const { data } = await api.post("/privacy/opt-out", { optOut: next });
      setPrivacyOptOut(Boolean(data.optOut));
      toast.success(data.message || "Privacy preference saved.");
      loadData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save your privacy preference."));
    } finally {
      setPrivacySaving(false);
    }
  };

  const confirmCancelMembership = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/membership/cancel");
      toast.success(data.message || "Membership cancellation processed.");
      setCancelOpen(false);
      refresh();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Cancellation failed."));
    } finally {
      setCancelling(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, notificationsRes] = await Promise.all([
        api.get("/orders"),
        api.get("/notifications"),
      ]);
      setOrders(ordersRes.data.orders);
      setQuota(ordersRes.data.quota || null);
      setNotifications(notificationsRes.data.notifications);
    } catch (error) {
      // Auth errors are surfaced via the API interceptor; log the rest for debugging.
      console.warn("Dashboard data refresh failed:", error?.message || error);
    }
  }, []);

  useEffect(() => {
    loadData();
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/privacy/status").then(({ data }) => setPrivacyOptOut(Boolean(data.optOut))).catch(() => {});
  }, [loadData]);

  // While any upload is still extracting in the background, poll until it
  // resolves so the member sees verified/mismatch without refreshing.
  useEffect(() => {
    const hasProcessing = orders.some((order) => order.verification_status === "processing");
    if (!hasProcessing) return undefined;
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [orders, loadData]);

  // Verify Stripe payment after the success redirect (idempotent server-side).
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const membershipFlow = searchParams.get("membership") === "success";
    const reportsFlow = searchParams.get("reports") === "success";
    const cancelled =
      searchParams.get("reports") === "cancelled" || searchParams.get("membership") === "cancelled";

    if (sessionId && (membershipFlow || reportsFlow)) {
      const verify = async (attempt = 1) => {
        try {
          const { data } = await api.post("/payments/verify", { sessionId });
          if (data.success) {
            toast.success(
              membershipFlow
                ? "Payment confirmed — your membership is now active."
                : "Payment confirmed — your report requests are in the operations queue."
            );
            refresh();
            loadData();
          } else if (attempt < 3) {
            setTimeout(() => verify(attempt + 1), 2500);
            return;
          } else {
            toast.info(data.message || "Payment is still processing. Refresh in a moment.");
          }
        } catch (error) {
          toast.error(apiErrorMessage(error, "Payment verification failed. Contact support if you were charged."));
        }
        setSearchParams({}, { replace: true });
      };
      verify();
    } else if (cancelled) {
      toast.info("Checkout was cancelled. No payment was taken.");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = membership?.status === "active";

  const openReview = (order) => {
    setReviewOrder(order);
    setReviewOpen(true);
  };

  const afterChange = () => {
    loadData();
    refresh();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Membership status banner */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Card
            className={`border ${
              isActive
                ? "border-[hsl(var(--status-verified)/0.35)] bg-[hsl(var(--status-verified)/0.06)]"
                : "border-[hsl(var(--status-mismatch)/0.35)] bg-[hsl(var(--status-mismatch)/0.06)]"
            }`}
            data-testid="membership-status-banner"
          >
            <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5">
              <div className="flex items-start gap-3">
                {isActive ? (
                  <ShieldCheck className="h-6 w-6 text-[hsl(var(--status-verified))] mt-0.5" />
                ) : (
                  <ShieldOff className="h-6 w-6 text-[hsl(var(--status-mismatch))] mt-0.5" />
                )}
                <div>
                  <p className="font-display font-semibold">
                    {isActive
                      ? membership.billing_interval === "trial"
                        ? `${membership.tier.charAt(0).toUpperCase() + membership.tier.slice(1)} beta trial active`
                        : `${membership.tier.charAt(0).toUpperCase() + membership.tier.slice(1)} membership active`
                      : membership
                      ? `Membership ${membership.status}`
                      : "No active membership"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isActive
                      ? membership.billing_interval === "trial"
                        ? `Free beta trial${
                            membership.current_period_end
                              ? ` — ${Math.max(
                                  0,
                                  Math.ceil(
                                    (new Date(membership.current_period_end) - Date.now()) / 86400000
                                  )
                                )} day(s) left (ends ${new Date(
                                  membership.current_period_end
                                ).toLocaleDateString()})`
                              : ""
                          }. Report requests are billed separately. Subscribe any time to keep access after the trial.`
                        : membership.cancel_at_period_end
                        ? "Cancellation scheduled — you keep full access until the end of the current period."
                        : `Billing: ${membership.billing_interval}. You can submit new QSIP report requests.`
                      : "An active Private Membership is required to submit new report requests."}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Signed in as {user?.email}</p>
                  {isActive && quota && (
                    <p className="text-xs mt-1.5" data-testid="monthly-quota-indicator">
                      <span
                        className={`font-medium ${
                          quota.limit !== null && quota.used >= quota.limit
                            ? "text-[hsl(var(--status-mismatch))]"
                            : "text-primary"
                        }`}
                      >
                        {quota.limit === null
                          ? "Unlimited report submissions"
                          : `Reports this month: ${quota.used} of ${quota.limit} used`}
                      </span>
                      {quota.limit !== null && quota.used >= quota.limit && (
                        <span className="text-muted-foreground">
                          {" "}
                          — resets on the 1st, or upgrade for a higher allowance.
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              {isActive ? (
                <div className="flex flex-wrap items-center gap-2">
                  {membership.billing_interval === "trial" ? (
                    <Button
                      size="sm"
                      data-testid="banner-subscribe-button"
                      onClick={() => navigate("/membership")}
                    >
                      Subscribe now <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="banner-change-plan-button"
                      onClick={() => navigate("/membership")}
                    >
                      <Settings2 className="h-4 w-4 mr-1.5" /> Change plan
                    </Button>
                  )}
                  {!membership.cancel_at_period_end && membership.billing_interval !== "trial" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      data-testid="banner-cancel-membership-button"
                      onClick={() => setCancelOpen(true)}
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Cancel membership
                    </Button>
                  )}
                </div>
              ) : (
                <Button data-testid="banner-get-membership-button" onClick={() => navigate("/membership")}>
                  Get membership <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Bento grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            {isActive ? (
              <OrderForm onCreated={afterChange} />
            ) : (
              <Card className="bg-card border-border" data-testid="order-form-locked-card">
                <CardContent className="p-10 text-center space-y-3">
                  <ShieldOff className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="font-display font-semibold">Order Your QSIP is locked</p>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Purchase a Basic, Pro, or Enterprise membership to unlock the document verification and report
                    request workflow.
                  </p>
                  <Button onClick={() => navigate("/membership")} data-testid="locked-get-membership-button">
                    View membership tiers
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="space-y-6">
            <CartPanel orders={orders} config={config} membership={membership} onCheckedOut={afterChange} />
            <NotificationsPanel notifications={notifications} onRefresh={loadData} />
            <Card className="bg-card border-border" data-testid="privacy-choices-card">
              <CardContent className="p-4 space-y-2.5">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" /> Privacy choices
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {privacyOptOut
                    ? "Your opt-out is active — we will not sell or share your personal information."
                    : "You can opt out of the sale or sharing of your personal information at any time."}
                </p>
                <Button
                  size="sm"
                  variant={privacyOptOut ? "outline" : "secondary"}
                  disabled={privacySaving || privacyOptOut === null}
                  onClick={togglePrivacyOptOut}
                  data-testid="privacy-opt-out-button"
                >
                  {privacySaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  {privacyOptOut ? "Remove my opt-out" : "Opt out of sale/sharing"}
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  For deletion, access, or correction requests, use the{" "}
                  <a href="/privacy-request" className="text-primary underline underline-offset-2" data-testid="privacy-request-link">
                    privacy request form
                  </a>
                  .
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        <OrdersTable orders={orders} onReview={openReview} />
      </main>

      <MismatchReviewModal
        order={reviewOrder}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onConfirmed={afterChange}
      />

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Cancel your membership?</AlertDialogTitle>
            <AlertDialogDescription>
              If you are on a monthly plan, it will not renew and you keep full access until the end of the
              current billing period. Yearly plans keep access until the end of the prepaid term. You can
              rejoin any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-membership-keep-button">Keep membership</AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-membership-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelling}
              onClick={(event) => {
                event.preventDefault();
                confirmCancelMembership();
              }}
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />}
              Cancel membership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SiteFooter />
    </div>
  );
}
